import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
} from '@/types/raspadinha-campaign'
import {
  applyCampaignWideGeneratedSlots,
  ensureDateWinningSchedules,
  formatCampaignDateLabel,
  generateCampaignWideWinningTimeSlots,
  getContemplatedPrizeTitle,
  listSelectableContemplatedPrizes,
  MAX_AUTO_GENERATED_WINNERS,
  MIN_AUTO_GENERATED_WINNERS,
  buildCampaignDayNumberByDate,
  type CampaignWideGenerationPreview,
} from '@/lib/raspadinha-campaigns'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import prizeSelectStyles from './AutoGenerateTimeSlotsModal.module.css'
import styles from './RaspadinhaGeneralTimeSlotsGenerator.module.css'

type RaspadinhaGeneralTimeSlotsGeneratorProps = {
  schedules: RaspadinhaDayWinningSchedule[]
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  startsAt: string | null
  endsAt: string | null
  disabled?: boolean
  disabledReason?: string | null
  onSchedulesChange: (next: RaspadinhaDayWinningSchedule[]) => void
  onPendingChange?: (hasPending: boolean) => void
}

type GeneralGeneratorForm = {
  totalSlotCount: number
  startTime: string
  endTime: string
}

const DEFAULT_FORM: GeneralGeneratorForm = {
  totalSlotCount: 10,
  startTime: '09:00',
  endTime: '18:00',
}

function buildDefaultSelectedPrizeId(prizes: RaspadinhaContemplatedPrizeImage[]): string {
  return listSelectableContemplatedPrizes(prizes)[0]?.id ?? ''
}

export function RaspadinhaGeneralTimeSlotsGenerator({
  schedules,
  contemplatedPrizes,
  startsAt,
  endsAt,
  disabled = false,
  disabledReason = null,
  onSchedulesChange,
  onPendingChange,
}: RaspadinhaGeneralTimeSlotsGeneratorProps) {
  const [form, setForm] = useState<GeneralGeneratorForm>(DEFAULT_FORM)
  const [selectedPrizeId, setSelectedPrizeId] = useState('')
  const [preview, setPreview] = useState<CampaignWideGenerationPreview | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const resolvedSchedules = useMemo(
    () => ensureDateWinningSchedules(schedules, startsAt, endsAt),
    [schedules, startsAt, endsAt]
  )

  const selectablePrizes = useMemo(
    () => listSelectableContemplatedPrizes(contemplatedPrizes),
    [contemplatedPrizes]
  )

  const campaignDayCount = resolvedSchedules.length
  const dayNumberByDate = useMemo(
    () => buildCampaignDayNumberByDate(resolvedSchedules),
    [resolvedSchedules]
  )
  const selectedPrizeTitle = getContemplatedPrizeTitle(contemplatedPrizes, selectedPrizeId)

  useEffect(() => {
    setSelectedPrizeId(buildDefaultSelectedPrizeId(contemplatedPrizes))
  }, [contemplatedPrizes])

  useEffect(() => {
    onPendingChange?.(preview !== null && preview.totalSlots > 0)
  }, [onPendingChange, preview])

  useEffect(() => {
    if (preview) {
      setIsExpanded(true)
    }
  }, [preview])

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((previous) => !previous)
  }, [])

  const handleGenerate = useCallback(() => {
    setErrorMessage(null)

    if (disabled) {
      setErrorMessage(disabledReason ?? 'Confirme o período da campanha antes de gerar horários.')
      return
    }

    if (selectablePrizes.length === 0) {
      setErrorMessage('Configure ao menos um prêmio em Mídia e prêmios antes de gerar horários.')
      return
    }

    if (!selectedPrizeId) {
      setErrorMessage('Selecione o prêmio que será contemplado nos horários gerados.')
      return
    }

    const result = generateCampaignWideWinningTimeSlots({
      schedules: resolvedSchedules,
      totalSlotCount: form.totalSlotCount,
      startTime: form.startTime,
      endTime: form.endTime,
      contemplatedPrizeId: selectedPrizeId,
    })

    if (!result.ok) {
      setErrorMessage(result.error)
      return
    }

    setPreview(result.preview)
  }, [
    disabled,
    disabledReason,
    form.endTime,
    form.startTime,
    form.totalSlotCount,
    resolvedSchedules,
    selectablePrizes.length,
    selectedPrizeId,
  ])

  const handleApply = useCallback(() => {
    if (!preview) return

    onSchedulesChange(applyCampaignWideGeneratedSlots(resolvedSchedules, preview.slotsByDate))
    setPreview(null)
    setErrorMessage(null)
  }, [onSchedulesChange, preview, resolvedSchedules])

  const handleDiscardPreview = useCallback(() => {
    setPreview(null)
    setErrorMessage(null)
  }, [])

  if (campaignDayCount === 0) {
    return null
  }

  return (
    <div
      className={`${styles.card} ${isExpanded ? styles.cardExpanded : styles.cardCollapsed}`}
      aria-labelledby="general-time-slots-generator-title"
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderMain}>
          <div className={styles.cardTitleRow}>
            <Icon icon="mdi:calendar-multiselect" className={styles.cardTitleIcon} aria-hidden />
            <h3 id="general-time-slots-generator-title" className={styles.cardTitle}>
              Gerador de horários geral
            </h3>
            {preview ? (
              <span className={styles.pendingBadge}>
                {preview.totalSlots} aguardando aplicação
              </span>
            ) : null}
          </div>
          <p className={styles.cardDescription}>
            {isExpanded
              ? `Crie vários horários de uma vez para um prêmio. A quantidade informada será dividida de forma igual entre os ${campaignDayCount} dia(s) da campanha, com horários aleatórios dentro do intervalo escolhido em cada dia.`
              : `Ferramenta opcional para gerar horários em massa nos ${campaignDayCount} dia(s) da campanha.`}
          </p>
        </div>

        <button
          type="button"
          className={styles.expandButton}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls="general-time-slots-generator-panel"
        >
          <span>{isExpanded ? 'Recolher' : 'Expandir'}</span>
          <Icon
            icon="mdi:chevron-down"
            className={`${styles.expandButtonIcon} ${isExpanded ? styles.expandButtonIconExpanded : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {isExpanded ? (
      <div id="general-time-slots-generator-panel" className={styles.formGrid}>
        <div className={styles.formRowQuantityTime}>
          <div className={editStyles.editField}>
            <label className={editStyles.editLabel} htmlFor="general-generator-quantity">
              Quantidade total de horários
            </label>
            <input
              id="general-generator-quantity"
              type="number"
              className={editStyles.editInput}
              min={MIN_AUTO_GENERATED_WINNERS}
              max={campaignDayCount * MAX_AUTO_GENERATED_WINNERS}
              step={1}
              value={form.totalSlotCount}
              onChange={(event) => {
                setErrorMessage(null)
                setForm((previous) => ({
                  ...previous,
                  totalSlotCount: Number(event.target.value),
                }))
              }}
              disabled={disabled}
            />
            <p className={editStyles.editLabelHint}>
              Mínimo {MIN_AUTO_GENERATED_WINNERS}, máximo{' '}
              {campaignDayCount * MAX_AUTO_GENERATED_WINNERS} para esta campanha.
            </p>
          </div>

          <div className={editStyles.editField}>
            <label className={editStyles.editLabel} htmlFor="general-generator-start-time">
              Horário de início
            </label>
            <input
              id="general-generator-start-time"
              type="time"
              className={editStyles.editInput}
              value={form.startTime}
              onChange={(event) => {
                setErrorMessage(null)
                setForm((previous) => ({ ...previous, startTime: event.target.value }))
              }}
              disabled={disabled}
              required
            />
          </div>

          <div className={editStyles.editField}>
            <label className={editStyles.editLabel} htmlFor="general-generator-end-time">
              Horário final
            </label>
            <input
              id="general-generator-end-time"
              type="time"
              className={editStyles.editInput}
              value={form.endTime}
              onChange={(event) => {
                setErrorMessage(null)
                setForm((previous) => ({ ...previous, endTime: event.target.value }))
              }}
              disabled={disabled}
              required
            />
          </div>
        </div>

        <div className={styles.prizeSection}>
          <label className={editStyles.editLabel} id="general-generator-prize-label">
            Prêmio
          </label>
          <p className={editStyles.editLabelHint}>
            Todos os horários gerados contemplarão este prêmio.
          </p>

          {selectablePrizes.length === 0 ? (
            <p className={prizeSelectStyles.prizeEmptyState}>
              Nenhum prêmio disponível. Cadastre ao menos um prêmio com título em{' '}
              <strong>Mídia e prêmios</strong>.
            </p>
          ) : (
            <ul
              className={prizeSelectStyles.prizeList}
              role="listbox"
              aria-labelledby="general-generator-prize-label"
            >
              {selectablePrizes.map((prize) => {
                const isSelected = selectedPrizeId === prize.id

                return (
                  <li key={prize.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={disabled}
                      className={`${prizeSelectStyles.prizeOption} ${isSelected ? prizeSelectStyles.prizeOptionSelected : ''}`}
                      onClick={() => {
                        setErrorMessage(null)
                        setSelectedPrizeId(prize.id)
                      }}
                    >
                      <input
                        type="radio"
                        className={prizeSelectStyles.prizeRadio}
                        name="general-generator-prize"
                        value={prize.id}
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                        aria-hidden
                      />
                      {prize.image_url.trim() ? (
                        <img src={prize.image_url} alt="" className={prizeSelectStyles.prizeThumb} />
                      ) : (
                        <span className={prizeSelectStyles.prizeThumbPlaceholder} aria-hidden>
                          <Icon icon="mdi:gift-outline" />
                        </span>
                      )}
                      <span className={prizeSelectStyles.prizeInfo}>
                        <span className={prizeSelectStyles.prizeTitle}>{prize.title}</span>
                        <span className={prizeSelectStyles.prizeMeta}>
                          Limite de contemplações: {prize.max_contemplations}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.generateButton}
            onClick={handleGenerate}
            disabled={disabled || selectablePrizes.length === 0}
          >
            <Icon icon="mdi:auto-fix" aria-hidden />
            Gerar horários para todos os dias
          </button>

          {disabled && disabledReason ? (
            <p className={styles.disabledHint} role="status">
              {disabledReason}
            </p>
          ) : null}
        </div>

        {errorMessage ? (
          <p className={styles.errorMessage} role="alert">
            {errorMessage}
          </p>
        ) : null}

        {preview ? (
          <div className={styles.previewPanel} role="status">
            <div className={styles.previewHeader}>
              <div>
                <p className={styles.previewTitle}>
                  <Icon icon="mdi:clock-check-outline" aria-hidden />
                  {preview.totalSlots} horário(s) gerados — revise antes de aplicar
                </p>
                <p className={styles.previewHint}>
                  Prêmio: <strong>{selectedPrizeTitle}</strong>. Distribuição igual entre{' '}
                  {preview.distribution.length} dia(s). Clique em <strong>Aplicar horários</strong>{' '}
                  para adicionar à campanha.
                </p>
              </div>
              <div className={styles.previewActions}>
                <button
                  type="button"
                  className={styles.previewActionPrimary}
                  onClick={handleApply}
                >
                  <Icon icon="mdi:content-save-check-outline" aria-hidden />
                  Aplicar horários
                </button>
                <button
                  type="button"
                  className={styles.previewActionSecondary}
                  onClick={handleDiscardPreview}
                >
                  Descartar
                </button>
              </div>
            </div>

            <ul className={styles.previewList}>
              {preview.distribution.map((entry) => (
                <li key={entry.date} className={styles.previewListItem}>
                  <span className={styles.previewDayLabel}>
                    {formatCampaignDateLabel(
                      entry.date,
                      dayNumberByDate.get(entry.date)
                    )}
                  </span>
                  <span className={styles.previewDayMeta}>
                    {entry.count} horário(s) ·{' '}
                    {(preview.slotsByDate[entry.date] ?? [])
                      .map((slot) => slot.time)
                      .join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  )
}
